import { Button, SvgIcon } from "@mui/material";
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, useGridApiContext } from "@mui/x-data-grid";
import React, { useEffect, useRef } from "react";
import { TEKS_TABEL_ID, LABEL_SESUAIKAN_KOLOM } from "../../services/tabelIndonesia";

/* Tinggi baris DataGrid.
 *
 * Sengaja memakai tinggi bawaan density "compact" (±36px), sama dengan
 * produksi. Dulu baris dipaksa 52px nyata karena tombol aksi 44px terpotong,
 * tetapi tombol di dalam sel kini diringkas 28px oleh index.css, jadi baris
 * setinggi itu hanya membuat tabel renggang. Pemanggil yang butuh tinggi lain
 * masih bisa mengirim prop rowHeight - ingat MUI mengalikannya dengan faktor
 * density compact (0,7).
 */

export default function ClientDataGrid({
  columns,
  rows,
  isLoading,
  initialState,
  rowCount,
  pagination = true,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  autoResize = true,
  paginationMode = "client",
  rowHeight,
  ukurUlangTinggiBaris = false,
  ...otherProps
}) {
  const apiRef = useRef();

  /*
   * Untuk tabel yang barisnya "auto" (teks dibungkus): MUI mengukur tinggi
   * baris saat sel masih selebar-lebarnya, sebelum kolom flex menyempit,
   * sehingga baris tetap setinggi satu baris teks dan baris berikutnya
   * terpotong. Setelah data masuk, tingginya diminta ukur ulang. Hanya aktif
   * bila pemanggil memintanya - 43 pemanggil lain tidak terpengaruh.
   */
  useEffect(() => {
    if (ukurUlangTinggiBaris && !isLoading && rows && rows.length > 0 && apiRef.current) {
      const id = setTimeout(() => apiRef.current.resetRowHeights(), 150);
      return () => clearTimeout(id);
    }
  }, [ukurUlangTinggiBaris, isLoading, rows]);

  // Auto-resize columns when data changes or loading finishes
  useEffect(() => {
    if (autoResize && !isLoading && rows && rows.length > 0 && apiRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        apiRef.current.autosizeColumns({
          includeHeaders: true,
          includeOutliers: true,
        });
      }, 100);
    }
  }, [isLoading, rows, autoResize]);

  // Gunakan controlled pagination model langsung dari props
  const paginationModel = {
    page: page || 0,
    pageSize: pageSize || 10,
  };

  // Handle pagination model change - langsung call parent handlers
  const handlePaginationModelChange = (newModel) => {
    // Call external handlers jika disediakan
    if (onPageChange && newModel.page !== paginationModel.page) {
      onPageChange(newModel.page);
    }

    if (onPageSizeChange && newModel.pageSize !== paginationModel.pageSize) {
      onPageSizeChange(newModel.pageSize);
    }
  };

  const dataGridProps = {
    columns,
    rows,
    loading: isLoading,
    apiRef: apiRef,
    // Teks bawaan tabel dalam bahasa Indonesia; pemanggil masih bisa menimpa
    // lewat prop localeText sendiri karena otherProps disebar setelah ini.
    localeText: TEKS_TABEL_ID,
    // Lihat catatan tinggi baris di atas; tanpa prop, bawaan MUI yang berlaku.
    ...(rowHeight ? { rowHeight } : {}),
    initialState: {
      pagination: {
        paginationModel: {
          pageSize: pageSize || 10,
          page: page || 0,
        },
      },
      density: "compact",
      ...initialState,
    },
    slots: {
      toolbar: CustomToolbar,
    },
    pageSizeOptions,
    disableRowSelectionOnClick: true,
    disableDensitySelector: true,
    checkboxSelection: false,
    disableVirtualization: true,
    scrollbarSize: 15,
    ...otherProps,
  };

  // Konfigurasi pagination berdasarkan mode
  if (!pagination) {
    // Disable pagination
    dataGridProps.pagination = false;
  } else if (paginationMode === "server") {
    // Server-side pagination - gunakan untuk registration page
    dataGridProps.paginationMode = "server";
    dataGridProps.rowCount = rowCount;
    dataGridProps.paginationModel = paginationModel;
    dataGridProps.onPaginationModelChange = handlePaginationModelChange;
  }
  // Untuk client-side pagination, tidak perlu mengatur paginationMode atau handlers
  // Biarkan DataGrid mengelola pagination secara internal

  return <DataGrid {...dataGridProps} />;
}

// csvOptions opsional — bila tidak diberikan, perilaku ekspor tetap seperti semula, sehingga 43 pemanggil ClientDataGrid lainnya tidak terpengaruh.
function CustomToolbar({ csvOptions }) {
  const apiRef = useGridApiContext();
  const autosizeOptions = {
    includeHeaders: true,
    includeOutliers: true,
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport csvOptions={csvOptions} />
      <Button startIcon={<AutosizeIcon />} size="small" onClick={() => apiRef.current.autosizeColumns(autosizeOptions)}>
        {LABEL_SESUAIKAN_KOLOM}
      </Button>
    </GridToolbarContainer>
  );
}

function AutosizeIcon() {
  return (
    <SvgIcon>
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#4596ED">
        <path d="M560-280h200v-200h-80v120H560v80ZM200-480h80v-120h120v-80H200v200Zm-40 320q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H160v480Zm0 0v-480 480Z" />
      </svg>
    </SvgIcon>
  );
}

// import { Button, SvgIcon } from '@mui/material';
// import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, useGridApiContext } from '@mui/x-data-grid'
// import React from 'react'

// export default function ClientDataGrid({ columns, rows, isLoading, initialState }) {
//     return (
//         <DataGrid
//             on
//             columns={columns}
//             rows={rows}
//             loading={isLoading}
//             initialState={{
//                 pagination: {
//                     paginationModel: {
//                         pageSize: 10,
//                     },
//                 },
//                 density: 'compact',
//                 ...initialState,
//             }}
//             slots={{
//                 toolbar: CustomToolbar,
//             }}
//             pageSizeOptions={[10, 20, 50, 100]}
//             disableRowSelectionOnClick
//             disableDensitySelector
//             checkboxSelection={false}
//             disableVirtualization
//             scrollbarSize={15}

//         />
//     )
// }

// function CustomToolbar() {
//     const apiRef = useGridApiContext();
//     const autosizeOptions = {
//         includeHeaders: true,
//         includeOutliers: true,
//     };
//     return (
//         <GridToolbarContainer>
//             <GridToolbarColumnsButton />
//             <GridToolbarFilterButton />
//             <GridToolbarExport/>
//             <Button startIcon={<AutosizeIcon/>} size="small" onClick={() => apiRef.current.autosizeColumns(autosizeOptions)}>
//                 Autosize columns
//             </Button>
//         </GridToolbarContainer>
//     );
// }

// function AutosizeIcon() {
//     return (
//         <SvgIcon>
//             <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#4596ED"><path d="M560-280h200v-200h-80v120H560v80ZM200-480h80v-120h120v-80H200v200Zm-40 320q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H160v480Zm0 0v-480 480Z"/></svg>
//         </SvgIcon>
//     );
// }
